import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useAuth } from './AuthContext';
import { 
  ArrowLeft, Wallet, CreditCard, 
  DollarSign, TrendingUp, Users, Calendar,
  CheckCircle, Clock, AlertCircle, ExternalLink,
  Loader2, BarChart3, FileText, Coins
} from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPayments = ({ onBack }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [reports, setReports] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    if (token) {
      Promise.all([loadPaymentStats(), loadTransactions(), loadReports()])
        .finally(() => setLoading(false));
    }
  }, [token]);

  const loadPaymentStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading payment stats:', error);
      setStats({ total_revenue: 0, total_subscribers: 0, active_subscriptions: 0, mrr: 0, this_month: 0, pending_payouts: 0 });
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await axios.get(`${API}/admin/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data);
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTransactions([]);
    }
  };

  const loadReports = async () => {
    try {
      const response = await axios.get(`${API}/admin/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  const formatCurrency = (value) => `R$ ${(value || 0).toFixed(2).replace('.', ',')}`;

  const getStatusBadge = (status) => {
    const styles = {
      paid: { bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', text: 'Pago' },
      pending: { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', text: 'Pendente' },
      failed: { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', text: 'Falhou' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span style={{ background: style.bg, color: style.color, padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600' }}>
        {style.text}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    const colors = { essencial: '#3b82f6', premium: '#00ffff', empresarial: '#8b5cf6', basico: '#3b82f6' };
    return (
      <span style={{ background: `${colors[plan] || '#666'}20`, color: colors[plan] || '#666', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '600', textTransform: 'capitalize' }}>
        {plan}
      </span>
    );
  };

  const planLabels = { free: 'Gratuito', basico: 'Essencial', premium: 'Premium', empresarial: 'Empresarial' };
  const planColors = { free: '#888', basico: '#3b82f6', premium: '#00ffff', empresarial: '#8b5cf6' };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00ffff' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1200px', margin: '0 auto' }} data-testid="admin-panel">
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <Button variant="ghost" onClick={onBack} style={{ marginBottom: '1rem' }} data-testid="admin-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <h1 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '0.5rem' }}>
          Gestão de Pagamentos
        </h1>
        <p style={{ color: '#aaa' }}>Visualize receitas, assinaturas e relatórios avançados</p>

        {/* Section Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          {[
            { id: 'overview', label: 'Visão Geral', icon: <BarChart3 className="w-4 h-4" /> },
            { id: 'reports', label: 'Relatórios', icon: <FileText className="w-4 h-4" /> },
            { id: 'transactions', label: 'Transações', icon: <Calendar className="w-4 h-4" /> },
            { id: 'stripe', label: 'Stripe', icon: <CreditCard className="w-4 h-4" /> }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeSection === tab.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveSection(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              style={activeSection === tab.id ? { background: '#00ffff', color: '#000' } : {}}
            >
              {tab.icon}
              <span style={{ marginLeft: '0.5rem' }}>{tab.label}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && (
        <>
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            {[
              { label: 'Receita Total', value: formatCurrency(stats?.total_revenue), color: '#22c55e', icon: <DollarSign className="w-5 h-5" style={{ color: '#fff' }} /> },
              { label: 'MRR', value: formatCurrency(stats?.mrr), color: '#00ffff', icon: <TrendingUp className="w-5 h-5" style={{ color: '#fff' }} /> },
              { label: 'Assinantes Ativos', value: stats?.active_subscriptions || 0, color: '#8b5cf6', icon: <Users className="w-5 h-5" style={{ color: '#fff' }} /> },
              { label: 'Este Mês', value: formatCurrency(stats?.this_month), color: '#f59e0b', icon: <Wallet className="w-5 h-5" style={{ color: '#fff' }} /> }
            ].map((card, i) => (
              <Card key={i} style={{ background: '#1a1a1a', border: '1px solid #333' }}>
                <CardContent style={{ padding: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: card.color, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.9 }}>
                      {card.icon}
                    </div>
                    <div>
                      <p style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem' }}>{card.label}</p>
                      <p style={{ color: card.color, fontSize: '1.25rem', fontWeight: '700' }}>{card.value}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Stats from Reports */}
          {reports && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
              <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
                <CardContent style={{ padding: '1.25rem' }}>
                  <p style={{ color: '#aaa', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Total Usuários</p>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700' }}>{reports.total_users}</p>
                </CardContent>
              </Card>
              <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
                <CardContent style={{ padding: '1.25rem' }}>
                  <p style={{ color: '#aaa', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Créditos Usados (Mês)</p>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f59e0b' }}>{reports.total_credits_used}</p>
                </CardContent>
              </Card>
              <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
                <CardContent style={{ padding: '1.25rem' }}>
                  <p style={{ color: '#aaa', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Créditos Comprados</p>
                  <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#22c55e' }}>{reports.total_purchased_credits}</p>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Reports Section */}
      {activeSection === 'reports' && reports && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Plan Distribution */}
          <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
            <CardHeader>
              <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Users className="w-5 h-5" style={{ color: '#00ffff' }} />
                Distribuição de Planos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {Object.entries(reports.plan_distribution).map(([plan, count]) => {
                  const pct = reports.total_users > 0 ? ((count / reports.total_users) * 100).toFixed(1) : 0;
                  return (
                    <div key={plan} data-testid={`plan-bar-${plan}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{ color: planColors[plan], fontWeight: '600', fontSize: '0.85rem' }}>{planLabels[plan]}</span>
                        <span style={{ color: '#888', fontSize: '0.85rem' }}>{count} ({pct}%)</span>
                      </div>
                      <div style={{ background: '#222', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                        <div style={{ background: planColors[plan], height: '100%', width: `${pct}%`, borderRadius: '4px', transition: 'width 0.5s ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Revenue by Type */}
          <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
            <CardHeader>
              <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Coins className="w-5 h-5" style={{ color: '#22c55e' }} />
                Receita por Tipo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                {[
                  { label: 'Assinaturas', value: reports.revenue_by_type.subscription, color: '#8b5cf6' },
                  { label: 'Créditos', value: reports.revenue_by_type.credits, color: '#f59e0b' },
                  { label: 'Consultoria', value: reports.revenue_by_type.consultancy, color: '#00ffff' }
                ].map(item => (
                  <div key={item.label} style={{ textAlign: 'center', padding: '1rem', background: '#0f0f0f', borderRadius: '8px' }}>
                    <p style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.5rem' }}>{item.label}</p>
                    <p style={{ color: item.color, fontSize: '1.25rem', fontWeight: '700' }}>{formatCurrency(item.value)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Revenue Trend */}
          <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
            <CardHeader>
              <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <TrendingUp className="w-5 h-5" style={{ color: '#00ffff' }} />
                Tendência de Receita (6 meses)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', height: '140px' }}>
                {reports.monthly_revenue.map((item, i) => {
                  const maxVal = Math.max(...reports.monthly_revenue.map(m => m.revenue), 1);
                  const height = Math.max((item.revenue / maxVal) * 120, 4);
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.65rem', color: '#888' }}>{formatCurrency(item.revenue)}</span>
                      <div style={{ width: '100%', height: `${height}px`, background: 'linear-gradient(180deg, #00ffff, #0066ff)', borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} />
                      <span style={{ fontSize: '0.65rem', color: '#999' }}>{item.month.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* User Growth */}
          <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
            <CardHeader>
              <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Users className="w-5 h-5" style={{ color: '#8b5cf6' }} />
                Crescimento de Usuários (6 meses)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', height: '140px' }}>
                {reports.user_growth.map((item, i) => {
                  const maxVal = Math.max(...reports.user_growth.map(m => m.users), 1);
                  const height = Math.max((item.users / maxVal) * 120, 4);
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                      <span style={{ fontSize: '0.7rem', color: '#aaa' }}>{item.users}</span>
                      <div style={{ width: '100%', height: `${height}px`, background: 'linear-gradient(180deg, #8b5cf6, #6d28d9)', borderRadius: '4px 4px 0 0', transition: 'height 0.5s ease' }} />
                      <span style={{ fontSize: '0.65rem', color: '#999' }}>{item.month.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Top Users */}
          {reports.top_users.length > 0 && (
            <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
              <CardHeader>
                <CardTitle style={{ fontSize: '1.1rem' }}>Usuários Mais Ativos</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {reports.top_users.map((u, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #222' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ color: '#00ffff', fontWeight: '700', width: '20px' }}>#{i + 1}</span>
                        <span style={{ color: '#fff', fontSize: '0.85rem' }}>{u.email}</span>
                        {getPlanBadge(u.plan)}
                      </div>
                      <span style={{ color: '#888', fontSize: '0.85rem' }}>{u.conversations} conversas</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Transactions Section */}
      {activeSection === 'transactions' && (
        <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
          <CardHeader>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Calendar className="w-5 h-5" style={{ color: '#00ffff' }} />
                <CardTitle style={{ fontSize: '1.1rem' }}>Transacoes Recentes</CardTitle>
              </div>
              <Badge variant="secondary">{transactions.length} transações</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #333' }}>
                    {['CLIENTE', 'PLANO', 'VALOR', 'STATUS', 'DATA'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '0.75rem', color: '#888', fontSize: '0.75rem', fontWeight: '600' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} style={{ borderBottom: '1px solid #222' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>{tx.email}</td>
                      <td style={{ padding: '0.75rem' }}>{getPlanBadge(tx.plan)}</td>
                      <td style={{ padding: '0.75rem', color: '#22c55e', fontWeight: '600', fontSize: '0.85rem' }}>{formatCurrency(tx.amount)}</td>
                      <td style={{ padding: '0.75rem' }}>{getStatusBadge(tx.status)}</td>
                      <td style={{ padding: '0.75rem', color: '#888', fontSize: '0.85rem' }}>{tx.date ? new Date(tx.date).toLocaleDateString('pt-BR') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {transactions.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                <Wallet className="w-12 h-12 mx-auto mb-2" style={{ opacity: 0.5 }} />
                <p>Nenhuma transação ainda</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stripe Section */}
      {activeSection === 'stripe' && (
        <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
          <CardHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <CreditCard className="w-5 h-5" style={{ color: '#00ffff' }} />
              <CardTitle style={{ fontSize: '1.1rem' }}>Configuração do Stripe</CardTitle>
            </div>
            <CardDescription>Gerencie sua integração de pagamentos</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <CheckCircle className="w-4 h-4" style={{ color: '#22c55e' }} />
                <span style={{ color: '#22c55e', fontWeight: '600' }}>Stripe Conectado</span>
              </div>
              <p style={{ color: '#aaa', fontSize: '0.85rem' }}>Sua conta Stripe está configurada e pronta para receber pagamentos.</p>
            </div>

            <div style={{ background: '#0f0f0f', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
              <p style={{ color: '#00ffff', fontWeight: '600', marginBottom: '0.75rem' }}>Eventos do Webhook configurados:</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {['checkout.session.completed', 'invoice.paid', 'customer.subscription.updated', 'customer.subscription.deleted'].map(evt => (
                  <Badge key={evt} variant="secondary" style={{ fontSize: '0.7rem' }}>{evt}</Badge>
                ))}
              </div>
            </div>

            <Button variant="outline" onClick={() => window.open('https://dashboard.stripe.com', '_blank')} style={{ width: 'fit-content' }}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Acessar Dashboard do Stripe
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminPayments;
